import { useMemo, useState } from 'react';

import { IconBed, IconBuilding, IconCalendar, IconChevron, IconFileSpreadsheet, IconLogout, IconUsers } from '../../components/layout/icons';
import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ApiError } from '../../lib/api-client';
import { useToast } from '../../lib/toast';
import { useDischargeSummary } from './mrd.queries';
import type { DischargeSummaryDepartmentRow } from './types';

const barWidth = (value: number, max: number): number => {
  if (max <= 0 || value <= 0) return 0;
  return Math.max(6, Math.round((value / max) * 100));
};

const toCount = (value: string): number => {
  const parsed = Math.floor(Number(value));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const DischargeCell = ({ value, max }: { value: number; max: number }) => (
  <div className="discharge-cell">
    <span className="discharge-count-badge">{value}</span>
    <span className="discharge-bar-track"><span className="discharge-bar-fill" style={{ width: `${barWidth(value, max)}%` }} /></span>
  </div>
);

const DischargeSummaryRow = ({ row, index, maxIpd, maxDaycare, maxTotal }: {
  row: DischargeSummaryDepartmentRow; index: number; maxIpd: number; maxDaycare: number; maxTotal: number;
}) => (
  <tr>
    <td className="discharge-row-index"><span>{index}</span></td>
    <td><span className="discharge-dept-inner"><span className="dept-icon" aria-hidden="true"><IconBuilding /></span>{row.departmentName}</span></td>
    <td><DischargeCell value={row.pendingIpd} max={maxIpd} /></td>
    <td><DischargeCell value={row.pendingDaycare} max={maxDaycare} /></td>
    <td><DischargeCell value={row.totalPending} max={maxTotal} /></td>
  </tr>
);

export const DischargeSummaryPage = () => {
  const [dischargeCount, setDischargeCount] = useState(0);
  const { showToast } = useToast();
  const summaryQuery = useDischargeSummary();
  const summary = summaryQuery.data;
  const rows = useMemo(() => summary?.departments ?? [], [summary]);
  const totals = summary?.totals;
  const minimumDischarge = totals?.minimumDischarge ?? 0;

  const maxIpd = useMemo(() => Math.max(1, ...rows.map((row) => row.pendingIpd)), [rows]);
  const maxDaycare = useMemo(() => Math.max(1, ...rows.map((row) => row.pendingDaycare)), [rows]);
  const maxTotal = useMemo(() => Math.max(1, ...rows.map((row) => row.totalPending)), [rows]);

  const errorMessage = summaryQuery.error instanceof ApiError ? summaryQuery.error.message : 'Discharge summary could not be loaded.';

  const submitDischarge = () => {
    if (dischargeCount < minimumDischarge) {
      showToast(`Discharge count cannot be less than the DAYCARE total (${minimumDischarge}).`, 'warning');
      return;
    }
    showToast('Bulk discharge is not wired up yet — coming soon.', 'warning');
  };

  return (
    <div className="page-stack discharge-summary-page">
      <header className="content-card discharge-summary-header">
        <span className="discharge-summary-header-icon" aria-hidden="true"><IconFileSpreadsheet /></span>
        <div>
          <span className="discharge-summary-eyebrow"><IconBuilding aria-hidden="true" />MRD Module</span>
          <h1>E-Discharge Summary</h1>
          <p>Department-wise IPD and DAYCARE records — pending and completed discharges</p>
        </div>
      </header>

      <section className="stat-grid discharge-stat-grid" aria-label="Discharge overview">
        <article className="discharge-stat-card">
          <div className="discharge-stat-head"><IconCalendar aria-hidden="true" /><span>Pending IPD</span></div>
          <strong>{totals?.pendingIpd ?? 0}</strong>
          <small>Pending IPD patients</small>
        </article>
        <article className="discharge-stat-card accent-amber">
          <div className="discharge-stat-head"><IconBed aria-hidden="true" /><span>Pending Daycare</span></div>
          <strong>{totals?.pendingDaycare ?? 0}</strong>
          <small>Must be discharged same day</small>
        </article>
        <article className="discharge-stat-card accent-red">
          <div className="discharge-stat-head"><IconChevron aria-hidden="true" /><span>Minimum Discharge</span></div>
          <strong>{minimumDischarge}</strong>
          <small>Cannot be less than DAYCARE total</small>
        </article>
        <article className="discharge-stat-card accent-green">
          <div className="discharge-stat-head"><IconUsers aria-hidden="true" /><span>Discharged</span></div>
          <strong>{totals?.discharged ?? 0}</strong>
          <small>Successfully processed</small>
        </article>
      </section>

      {summaryQuery.isLoading ? <LoadingState rows={8} columns={4} label="Loading discharge summary" /> : null}
      {summaryQuery.isError ? <ErrorState title="Unable to load discharge summary" message={errorMessage} action={{ label: 'Try again', onClick: () => void summaryQuery.refetch() }} /> : null}

      {summaryQuery.isSuccess ? (
        <section className="content-card discharge-summary-card">
          <div className="discharge-summary-card-head">
            <h2><IconBuilding aria-hidden="true" />Department-wise Pending Discharges</h2>
            <div className="discharge-summary-pills">
              <span className="discharge-pill">{rows.length} departments</span>
              <span className="discharge-pill accent-amber">{totals?.pendingIpd ?? 0} IPD pending</span>
              <span className="discharge-pill accent-blue">{totals?.pendingDaycare ?? 0} DAYCARE pending</span>
            </div>
          </div>

          <div className="discharge-table-wrap">
            <table className="discharge-summary-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Department</th>
                  <th>Pending IPD</th>
                  <th>Pending Daycare</th>
                  <th>Total Pending</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <DischargeSummaryRow key={row.departmentId} row={row} index={index + 1} maxIpd={maxIpd} maxDaycare={maxDaycare} maxTotal={maxTotal} />
                ))}
                {rows.length === 0 ? <tr><td className="discharge-summary-empty" colSpan={5}>No bed-owning departments configured.</td></tr> : null}
              </tbody>
              {rows.length > 0 ? (
                <tfoot>
                  <tr>
                    <td colSpan={2}>Totals</td>
                    <td>{totals?.pendingIpd ?? 0}</td>
                    <td>{totals?.pendingDaycare ?? 0}</td>
                    <td>{totals?.totalPending ?? 0}</td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>

          <div className="discharge-summary-footer">
            <span className="discharge-pill accent-primary">Total pending {totals?.totalPending ?? 0}</span>
            <div className="discharge-summary-footer-action">
              <label className="discharge-count-field">
                <span>Discharge count</span>
                <input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={dischargeCount}
                  onChange={(event) => setDischargeCount(toCount(event.target.value))}
                />
                <small>Minimum: {minimumDischarge} (all DAYCARE)</small>
              </label>
              <button type="button" className="button button-primary discharge-summary-submit" onClick={submitDischarge}>
                <IconLogout aria-hidden="true" />Discharge Patients
              </button>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
};
